import React, { useState, useRef, useCallback } from 'react';

export function ClockTimePicker({ value, onChange, label }) {
  // value format: "H:MM:AP" e.g. "7:00:AM"
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('hour'); // 'hour' or 'minute'

  const h = parseInt(value?.split(':')[0]) || 12;
  const m = value?.split(':')[1] || '00';
  const ap = value?.split(':')[2] || 'AM';

  const hours = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
  const minutes = ['00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55'];

  const setHour = (hr) => {
    onChange(`${hr}:${m}:${ap}`);
    setMode('minute');
  };
  const setMinute = (min) => {
    onChange(`${h}:${min}:${ap}`);
  };
  const setAmPm = (v) => {
    onChange(`${h}:${m}:${v}`);
  };

  const displayH = String(h).padStart(2, '0');
  const displayM = m;

  // Calculate hand angle
  const currentVal = mode === 'hour' ? h : parseInt(m);
  const totalPositions = mode === 'hour' ? 12 : 60;
  const angle = (currentVal / totalPositions) * 360 - 90;

  // Map value to position index (0-11) for the dot
  const getIndex = () => {
    if (mode === 'hour') return hours.indexOf(h);
    return minutes.indexOf(m) !== -1 ? minutes.indexOf(m) : -1;
  };

  const clockRef = useRef(null);

  const handleClockClick = useCallback((e) => {
    const rect = clockRef.current?.getBoundingClientRect();
    if (!rect) return;
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = e.clientX - cx;
    const dy = e.clientY - cy;
    let deg = Math.atan2(dy, dx) * (180 / Math.PI) + 90;
    if (deg < 0) deg += 360;

    if (mode === 'hour') {
      const idx = Math.round(deg / 30) % 12;
      setHour(hours[idx]);
    } else {
      const idx = Math.round(deg / 30) % 12;
      setMinute(minutes[idx]);
    }
  }, [mode, h, m, ap, onChange]);

  const display = `${h}:${m} ${ap}`;

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => { setOpen(true); setMode('hour'); }}
        className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2 bg-white hover:border-[#2E7D32] transition-colors text-left min-w-[140px]"
        data-testid={`clock-picker-trigger-${label?.replace(/\s/g, '-')?.toLowerCase() || 'time'}`}
      >
        <svg className="w-4 h-4 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>
        </svg>
        <span className="text-sm font-medium text-gray-800">{value ? display : 'Select time'}</span>
      </button>
    );
  }

  const posIdx = getIndex();

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/30" onClick={() => setOpen(false)}>
      <div
        className="bg-white rounded-2xl shadow-2xl w-[280px] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="clock-picker-dialog"
      >
        {/* Digital display header */}
        <div className="bg-[#2E7D32] px-5 py-4">
          <p className="text-[10px] uppercase tracking-[0.15em] text-white/70 mb-1">Select time</p>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setMode('hour')}
              className={`text-4xl font-bold transition-colors ${mode === 'hour' ? 'text-white' : 'text-white/40'}`}
            >
              {displayH}
            </button>
            <span className="text-4xl font-bold text-white/40">:</span>
            <button
              type="button"
              onClick={() => setMode('minute')}
              className={`text-4xl font-bold transition-colors ${mode === 'minute' ? 'text-white' : 'text-white/40'}`}
            >
              {displayM}
            </button>
            <div className="flex flex-col ml-2 gap-0.5">
              <button
                type="button"
                onClick={() => setAmPm('AM')}
                className={`text-xs font-bold px-1.5 py-0.5 rounded transition-colors ${ap === 'AM' ? 'bg-white/20 text-white' : 'text-white/40'}`}
              >AM</button>
              <button
                type="button"
                onClick={() => setAmPm('PM')}
                className={`text-xs font-bold px-1.5 py-0.5 rounded transition-colors ${ap === 'PM' ? 'bg-white/20 text-white' : 'text-white/40'}`}
              >PM</button>
            </div>
          </div>
        </div>

        {/* Clock face */}
        <div className="px-5 py-5">
          <div
            ref={clockRef}
            className="relative w-[220px] h-[220px] mx-auto bg-gray-100 rounded-full cursor-pointer"
            onClick={handleClockClick}
          >
            {/* Center dot */}
            <div className="absolute top-1/2 left-1/2 w-2 h-2 bg-[#2E7D32] rounded-full -translate-x-1/2 -translate-y-1/2 z-10" />

            {/* Hand line */}
            {posIdx >= 0 && (
              <div
                className="absolute top-1/2 left-1/2 origin-left h-[2px] bg-[#2E7D32] z-[5]"
                style={{
                  width: '75px',
                  transform: `rotate(${posIdx * 30 - 90}deg)`,
                }}
              />
            )}

            {/* Numbers around the clock */}
            {(mode === 'hour' ? hours : minutes).map((val, i) => {
              const a = (i * 30 - 90) * (Math.PI / 180);
              const r = 85;
              const x = 110 + r * Math.cos(a);
              const y = 110 + r * Math.sin(a);
              const isSelected = mode === 'hour' ? val === h : val === m;

              return (
                <button
                  key={val}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (mode === 'hour') setHour(val);
                    else setMinute(val);
                  }}
                  className={`absolute w-9 h-9 flex items-center justify-center rounded-full text-sm font-medium transition-all -translate-x-1/2 -translate-y-1/2
                    ${isSelected ? 'bg-[#2E7D32] text-white' : 'text-gray-700 hover:bg-gray-200'}`}
                  style={{ left: x, top: y }}
                >
                  {val}
                </button>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-5 pb-4">
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="text-xs font-bold uppercase tracking-wider text-gray-500 hover:text-gray-800 px-3 py-2"
          >Cancel</button>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="text-xs font-bold uppercase tracking-wider text-[#2E7D32] hover:text-[#1B5E20] px-3 py-2"
            data-testid="clock-picker-ok"
          >OK</button>
        </div>
      </div>
    </div>
  );
}
